'use client';

import { createContext, ReactNode, useContext, useRef } from 'react';
import { create, useStore } from 'zustand';
import { Envs } from '@ae/envs';
import { Locale } from '@ae/i18n';
import { useShallow } from 'zustand/react/shallow';
import { getApiEndpoint, getAssetsPath } from './helpers';
import { getLegacyAuthEndpoint, getLegacyLogoutEndpoint } from './types';

type PathConfigStoreState = {
  assetsPath: string;
  apiEndpoint: string;
  appBasePath: string;
};

type PathConfigStoreActions = {
  getApiEndpoint: (requestUrl: string) => string;
  getCssUrlPath: (url: string) => string;
  getLegacyAuthEndpoint: (url: string, locale: Locale) => string;
  getLegacyLogoutEndpoint: (url: string) => string;
};

type PathConfigStoreType = PathConfigStoreState & PathConfigStoreActions;

const createPathConfigStore = (
  initialValues: Partial<PathConfigStoreType> = {}
) =>
  create<PathConfigStoreType>((_, get) => ({
    assetsPath: initialValues.assetsPath || '',
    apiEndpoint: initialValues.apiEndpoint || '',
    appBasePath: initialValues.appBasePath || '',
    getApiEndpoint: (requestUrl: string) => {
      return getApiEndpoint(requestUrl, get().apiEndpoint);
    },
    getCssUrlPath: (url: string) => {
      return `url('${get().assetsPath || getAssetsPath()}${url}')`;
    },
    getLegacyAuthEndpoint: (requestUrl: string, locale: Locale) => {
      return getLegacyAuthEndpoint(
        requestUrl,
        get().getApiEndpoint(requestUrl),
        locale
      );
    },
    getLegacyLogoutEndpoint: (requestUrl: string) => {
      return getLegacyLogoutEndpoint(
        requestUrl,
        get().getApiEndpoint(requestUrl)
      );
    },
  }));

const PathConfigStoreContext = createContext<ReturnType<
  typeof createPathConfigStore
> | null>(null);

export type PathConfigProps = {
  assetsPath?: string;
  apiEndpoint?: string;
  appBasePath?: string;
  children?: ReactNode;
};

export const PathConfigProvider = ({
  assetsPath,
  apiEndpoint,
  appBasePath,
  children,
}: PathConfigProps) => {
  const store = useRef<ReturnType<typeof createPathConfigStore> | null>(null);

  if (!store.current) {
    store.current = createPathConfigStore({
      assetsPath: assetsPath ?? getAssetsPath(),
      apiEndpoint: apiEndpoint ?? Envs.apiEndpoint,
      appBasePath: appBasePath ?? Envs.basePath,
    });
  }

  return (
    <PathConfigStoreContext.Provider value={store.current}>
      {children}
    </PathConfigStoreContext.Provider>
  );
};

const usePathConfigStore = <T,>(
  selector: (state: PathConfigStoreType) => T
): T => {
  const context = useContext(PathConfigStoreContext);
  if (!context) {
    throw new Error(
      'usePathConfigStore must be used within a PathConfigStoreProvider'
    );
  }
  return useStore(context, useShallow(selector));
};

export const useGetApiEndpoint = (requestUrl: string): string =>
  usePathConfigStore((state) => state.getApiEndpoint(requestUrl));
export const useCssUrlPath = (): ((url: string) => string) =>
  usePathConfigStore((state) => state.getCssUrlPath);
export const useAssetsPath = (): string =>
  usePathConfigStore((state) => state.assetsPath);
export const useApiEndpoint = (): string =>
  usePathConfigStore((state) => state.apiEndpoint);
export const useAppBasePath = (): string =>
  usePathConfigStore((state) => state.appBasePath);
export const useLegacyAuthEndpoint = (): ((
  url: string,
  locale: Locale
) => string) => usePathConfigStore((state) => state.getLegacyAuthEndpoint);
export const useLegacyLogoutEndpoint = (): ((url: string) => string) =>
  usePathConfigStore((state) => state.getLegacyLogoutEndpoint);
